import React, { FC, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useSwipeable } from 'react-swipeable';
import { useMutation } from '@apollo/client';

import { useI18n } from '~Hooks';
import { updateOrderEstimate } from '../../Queries/__queries__';
import { UPDATE_ORDER_ESTIMATE } from '../../Queries/orders';
import { DateTime } from 'luxon';

const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  background-color: var(--colorBlueGray100);
  border-bottom: 1px solid var(--colorBlueGray200);
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  color: black;
  font-size: 22px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;

const TimeButton = styled.div<{ disabled?: boolean }>`
  background: linear-gradient(-180deg, var(--colorBlueGray1000) 0%, var(--colorBlueGray1200) 100%);
  border-radius: 4px;
  height: 48px;
  width: 88px;
  text-align: center;
  line-height: 47px;
  margin-left: 8px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'initial')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
`;

const Icon = styled.i`
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  color: var(--colorWhite);
`;

interface Props {
  estimatedFinishTime: string;
  onChangePickupTime?: (newTime: string) => void;
  orderId: string;
  disabled?: boolean; // May be true if parent Order has allowUserMutation = false
}

/**
 * Component that displays two buttons (+ & -) which enables the kitchen staff to +/- minutes to when the order will be ready - the time change is then visible for the customer in the app
 * We show it on app orders - Both for ASAP & scheduled orders
 */
const OrderETA: FC<Props> = ({ estimatedFinishTime, onChangePickupTime, orderId, disabled }) => {
  const { i18n } = useI18n();
  const [changeETA] = useMutation<updateOrderEstimate>(UPDATE_ORDER_ESTIMATE);

  const [touching, setTouching] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState(DateTime.now());
  const [diffMinutes, setDiffMinutes] = useState<number>(15);

  const currentETA = DateTime.fromISO(estimatedFinishTime);

  // Used to delay the mutation to prevent requests while user is actively updating value
  const mutationTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Blocks changes to the ETA when swiping
  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => setTouching(false),
  });

  const handleWrapperTouch = (e: React.UIEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setTouching(true);
  };

  // ** UPDATE CURRENT TIME ** Update current time every 10 seconds
  useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(DateTime.now()), 10000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // Get the time diff between the ETA and current time in minutes
    const ETAMinutes = Math.round(currentETA.diff(currentTime, 'minutes').minutes);
    setDiffMinutes(ETAMinutes);
  }, [currentTime, currentETA]);

  // inc = increment, dec = decrement
  const handleUserUpdate = (type: 'inc' | 'dec') => {
    // If component is disabled or the user is swiping, cancel the update
    if (disabled || !touching) {
      return;
    }

    let newETA = currentETA;

    if (type === 'inc') {
      newETA = currentETA.plus({ minutes: 5 });
    }
    if (type === 'dec' && diffMinutes > 1) {
      newETA = currentETA.minus({ minutes: diffMinutes > 5 ? 5 : 1 });
    }

    // Set new ETA to state
    if (newETA.isValid && onChangePickupTime) {
      onChangePickupTime(newETA.toISO());
    }

    // Clear any previous timeout, to prevent multiple calls
    clearTimeout(mutationTimeout.current);

    // Wait 5 seconds before doing the request, prevent unnecessary requests on quick increase/decreases in ETA
    mutationTimeout.current = setTimeout(() => {
      changeETA({
        variables: { id: orderId, estimatedFinishTime: newETA.toISO() },
      });
    }, 5000);
  };

  return (
    <Wrapper>
      <span>
        {diffMinutes} {i18n('KDS.Min')}
      </span>
      <ButtonWrapper {...handlers} onTouchStart={handleWrapperTouch}>
        <TimeButton onTouchEnd={() => handleUserUpdate('dec')} disabled={disabled}>
          <Icon className="icon minus" />
        </TimeButton>
        <TimeButton onTouchEnd={() => handleUserUpdate('inc')} disabled={disabled}>
          <Icon className="icon plus" />
        </TimeButton>
      </ButtonWrapper>
    </Wrapper>
  );
};

export default OrderETA;
